import { computed, type Ref, ref } from "vue";

import type { Doc, Task } from "~/shared/types";
import { useDataStore } from "~/stores";
import type { GetTaskByDuidOptions } from "~/stores/DataStore/task";
import { asyncState } from "~/utils/async";
import usePaginatedList from "~/utils/usePaginatedList";

const useAsyncEntity = <T>(
  entityDuid: Ref<string | undefined>,
  getEntity: (duid: string) => Promise<T | undefined>,
  getEntityIfLoaded: (duid: string) => T | undefined
) =>
  asyncState(async () => (entityDuid.value ? getEntity(entityDuid.value) : undefined), {
    default: entityDuid.value ? getEntityIfLoaded(entityDuid.value) : undefined,
    watch: entityDuid,
  });

export const useAsyncTask = (taskDuid: Ref<string | undefined>) => {
  const dataStore = useDataStore();
  const { state: task, isLoading } = useAsyncEntity<Task>(
    taskDuid,
    dataStore.getTaskByDuid,
    dataStore.getTaskByDuidIfLoaded
  );
  return { task, isLoading };
};

export const useAsyncDoc = (docDuid: Ref<string | undefined>) => {
  const dataStore = useDataStore();
  const { state: doc, isLoading } = useAsyncEntity<Doc>(
    docDuid,
    dataStore.getDocByDuid,
    dataStore.getDocByDuidIfLoaded
  );
  return { doc, isLoading };
};

export const useAsyncTasks = (taskDuids: Ref<string[]>, options: Ref<GetTaskByDuidOptions>) => {
  const dataStore = useDataStore();
  const { state: tasks, isLoading } = asyncState(
    async () => dataStore.getTasksByDuids(taskDuids.value, options.value),
    {
      default: dataStore.getTasksByDuidsIfLoaded(taskDuids.value, options.value),
      watch: [taskDuids, options],
    }
  );
  return { tasks, isLoading };
};

export const useAsyncTasksOrdered = (taskDuids: Ref<string[]>, options: Ref<GetTaskByDuidOptions>) => {
  const dataStore = useDataStore();
  const { state: tasks, isLoading } = asyncState(
    async () => dataStore.getTasksByDuidsOrdered(taskDuids.value, options.value),
    {
      default: dataStore.getTasksByDuidsOrderedIfLoaded(taskDuids.value, options.value),
      watch: [taskDuids, options],
    }
  );
  return { tasks, isLoading };
};

export const usePaginatedTasksWithSearch = (
  pageDuid: Ref<string>,
  scrollContainer: Ref<HTMLElement | null>,
  search: Ref<string>
) => {
  const dataStore = useDataStore();
  // TODO incremental here and for docs equivalent in TaskOrDocLinkDropdown.vue: this isn't reactive if you start typing a search before it loads. can we just make the whole thing computed?
  const lowerCaseSearch = search.value.toLowerCase();
  const searchedTasks = ref<Task[]>(
    dataStore.getAllLoadedTasksForUiOnly().filter((e) => e.title.toLowerCase().includes(lowerCaseSearch))
  );
  const count = computed(() => searchedTasks.value.length);
  const {
    isLoading,
    totalCount,
    canLoadMore,
    reset: baseReset,
  } = usePaginatedList(pageDuid, scrollContainer, count, async (loadedTasksCount) => {
    const { data } = await dataStore.loadTasks({ title: search.value }, loadedTasksCount, {
      key: `paginated-tasks-${pageDuid.value}`,
    });
    searchedTasks.value = Array.from(new Set([...searchedTasks.value, ...data.results]));
    return data.count;
  });
  const reset = () => {
    baseReset();
    searchedTasks.value = [];
  };
  return { searchedTasks, isLoading, totalCount, canLoadMore, reset };
};
